import { useState, useEffect, useRef } from 'react';
import { 
  FaNewspaper, 
  FaBullhorn, 
  FaChartLine,
  FaGlobe,
  FaInstagram,
  FaLinkedin,
  FaFacebook,
  FaYoutube,
  FaWhatsapp,
  FaBook,
  FaCheckCircle,
  FaArrowRight,
  FaSpinner,
  FaExclamationTriangle,
  FaUpload
} from 'react-icons/fa';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import Footer from '../components/Footer';

function MSMETimesPage() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    // Section 1: Basic Details
    fullName: '',
    companyName: '',
    email: '',
    mobileNumber: '',
    
    // Section 2: Business Profile
    businessCategory: '',
    otherCategory: '',
    cityState: '',
    
    // Section 3: Advertising Interest
    selectedPlatforms: [],
    
    // Magazine specific fields
    adSize: '',
    editionPreference: '',
    
    // Digital/Custom fields
    visibilityObjective: '',
    engagementDuration: '',
    additionalNotes: '',
    
    // Consent
    consentAccepted: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileError, setFileError] = useState('');
  
  // Success popup state
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  
  // CAPTCHA state
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(false);
  const recaptchaRef = useRef(null);

  // API URLs
  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/UImsme-times`;
  const API_MEDIA = `${process.env.REACT_APP_API_BASE_URL}/UImedia/upload`;
  const API_ENQUIRIES = `${process.env.REACT_APP_API_BASE_URL}/msme-times-enquiries`;
  
  // CAPTCHA Site Key
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  // Fetch dynamic data from API
  useEffect(() => {
    fetchMSMETimesData();
  }, []);

  // Auto redirect after successful submission
  useEffect(() => {
    if (showSuccessPopup) {
      const timer = setTimeout(() => {
        setShowSuccessPopup(false);
        resetForm();
        window.location.href = '/MSMETimes';
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showSuccessPopup]);

  const fetchMSMETimesData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(API_URL);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setPageData(data);
      } else {
        throw new Error('Invalid data structure received from API');
      }
    } catch (err) {
      console.error('Error fetching MSME Times data:', err);
      setError('Failed to load MSME Times data. Please try again later.');
      setPageData(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle CAPTCHA change
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setCaptchaError(false);
    
    if (!token) {
      setCaptchaError(true);
    }
  };

  // Handle CAPTCHA expiration
  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    setCaptchaError(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'selectedPlatforms') {
        const updatedPlatforms = formData.selectedPlatforms.includes(value)
          ? formData.selectedPlatforms.filter(platform => platform !== value)
          : [...formData.selectedPlatforms, value];
        setFormData({ ...formData, selectedPlatforms: updatedPlatforms });
      } else if (name === 'consentAccepted') {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Handle file upload - Just like in demo
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // File validation
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 
                         'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!allowedTypes.includes(file.type)) {
      setFileError('Only PDF, JPG, PNG, DOC files are allowed');
      return;
    }
    
    if (file.size > maxSize) {
      setFileError('File size should be less than 10MB');
      return;
    }
    
    setSelectedFile(file);
    setFileError('');
  };

  // Upload file to media endpoint and get media ID - EXACTLY like demo
  const uploadFileToServer = async (file) => {
    if (!file) return null;

    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('type', file.type);

      const response = await axios.post(API_MEDIA, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Return the media ID
      return response.data.id || response.data._id;
    } catch (error) {
      console.error('Error uploading file:', error);
      setFileError('Failed to upload file. Please try again.');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle form submission - Updated with file upload
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // CAPTCHA validation
    if (!captchaToken) {
      setCaptchaError(true);
      alert('Please complete the CAPTCHA verification');
      return;
    }
    
    // Basic validation
    if (!formData.consentAccepted) {
      alert('Please accept the consent to proceed');
      return;
    }
    
    setIsSubmitting(true);
    setFileError('');

    try {
      // Step 1: Upload file if exists
      let mediaId = null;
      if (selectedFile) {
        mediaId = await uploadFileToServer(selectedFile);
        if (!mediaId) {
          throw new Error('File upload failed');
        }
      }

      // Step 2: Prepare enquiry data
      const enquiryData = {
        // Basic details
        fullName: formData.fullName,
        companyName: formData.companyName,
        email: formData.email,
        mobileNumber: formData.mobileNumber,
        
        // Business profile
        businessCategory: formData.businessCategory,
        otherCategory: formData.otherCategory || '',
        cityState: formData.cityState || '',
        
        // Advertising interest
        selectedPlatforms: formData.selectedPlatforms,
        adSize: formData.adSize || '',
        editionPreference: formData.editionPreference || '',
        
        // Digital fields
        visibilityObjective: formData.visibilityObjective || '',
        engagementDuration: formData.engagementDuration || '',
        additionalNotes: formData.additionalNotes || '',
        
        // Media reference (from file upload)
        media: mediaId,
        
        // Consent
        consentAccepted: formData.consentAccepted,
        
        // CAPTCHA token
        recaptchaToken: captchaToken
      };

    //   console.log('Submitting enquiry:', enquiryData);
      
      // Step 3: Submit enquiry to backend
      const response = await axios.post(API_ENQUIRIES, enquiryData);
      
      if (response.data.success) {
        // Show success popup
        setShowSuccessPopup(true);
        
        // Reset CAPTCHA
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
      } else {
        throw new Error(response.data.message || 'Failed to submit enquiry');
      }
      
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Reset CAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaToken(null);
      
      setFileError(error.response?.data?.message || 'There was an error submitting your enquiry. Please try again.');
      alert('There was an error submitting your enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form function
  const resetForm = () => {
    setFormData({
      fullName: '',
      companyName: '',
      email: '',
      mobileNumber: '',
      businessCategory: '',
      otherCategory: '',
      cityState: '',
      selectedPlatforms: [],
      adSize: '',
      editionPreference: '',
      visibilityObjective: '',
      engagementDuration: '',
      additionalNotes: '',
      consentAccepted: false
    });
    setSelectedFile(null);
    setFileError('');
  };

  // Render social media icons dynamically
  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'whatsapp':
        return <FaWhatsapp />;
      case 'instagram':
        return <FaInstagram />;
      case 'linkedin':
        return <FaLinkedin />;
      case 'facebook':
        return <FaFacebook />;
      case 'youtube':
        return <FaYoutube />;
      default:
        return <FaGlobe />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-700 text-white flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-amber-500 text-4xl mx-auto mb-4" />
          <p className="text-amber-200 text-lg">Loading MSME Times...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !pageData) {
    return (
      <div className="min-h-screen bg-gray-700 text-white flex items-center justify-center">
        <div className="text-center max-w-md p-8 bg-gray-800 rounded-xl border border-amber-500/30">
          <FaExclamationTriangle className="text-amber-500 text-4xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-amber-300 mb-2">Unable to Load Content</h2>
          <p className="text-amber-200 mb-6">{error || 'No data available'}</p>
          <button
            onClick={fetchMSMETimesData}
            className="bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold py-2 px-6 rounded-lg hover:from-amber-600 hover:to-yellow-600 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Check if magazine is selected
  const isMagazineSelected = formData.selectedPlatforms.includes('magazine');
  
  // Check if any digital option is selected
  const isDigitalSelected = formData.selectedPlatforms.some(platform => 
    ['weekly', 'monthly', 'brandStory', 'event', 'website'].includes(platform)
  );

  return (
    <div className="min-h-screen bg-gray-700 text-white">
      {/* Hero Section */}
      <header className="relative py-20 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>

        <div className="relative max-w-6xl mx-auto text-center">
          <div className="inline-block bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold px-6 py-2 rounded-full mb-6">
            MSME Times
          </div>
          <p className="text-lg text-amber-300 mb-4">{pageData.hero?.tagline}</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white">
            {pageData.hero?.title}
          </h1>
          <p className="text-xl md:text-2xl text-amber-100 max-w-3xl mx-auto mb-12">
            {pageData.hero?.description}
          </p>
          
          <div className="mt-12 bg-gradient-to-br from-gray-800/70 to-gray-900/70 backdrop-blur-sm rounded-xl p-8 max-w-4xl mx-auto border border-amber-500/20">
            <h2 className="text-2xl font-bold mb-4 text-amber-300">{pageData.hero?.about?.title}</h2>
            <p className="text-lg text-white mb-4">
              {pageData.hero?.about?.content1}
            </p>
            <p className="text-lg text-amber-100">
              {pageData.hero?.about?.content2}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* What We Do Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-10 text-center text-amber-300">
            What We Do
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pageData.whatWeDo && pageData.whatWeDo.length > 0 ? (
              pageData.whatWeDo.map((item, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-800 to-gray-900/50 p-6 rounded-xl border border-amber-500/20 hover:border-amber-500/40 transition-all">
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold text-xl rounded-full mb-4">
                    {item.number}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-amber-200">{item.title}</h3>
                  <p className="text-amber-100">{item.description}</p>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center py-8">
                <p className="text-amber-300">No "What We Do" items available.</p>
              </div>
            )}
          </div>
        </section>

        {/* Target Audience Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-10 text-center text-amber-300">
            Who It's For
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {pageData.targetAudience && pageData.targetAudience.length > 0 ? (
              pageData.targetAudience.map((audience, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-800 to-gray-900/50 p-4 rounded-lg text-center border border-gray-600/50 hover:border-amber-500/30 transition-all">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold rounded-full mb-2 mx-auto">
                    {audience.number}
                  </div>
                  <p className="text-amber-100 font-medium">{audience.label}</p>
                </div>
              ))
            ) : (
              <div className="col-span-4 text-center py-4">
                <p className="text-amber-300">No target audience data available.</p>
              </div>
            )}
          </div>
        </section>

        {/* Magazine Section */}
        {pageData.magazine && (
          <section className="mb-16 bg-gradient-to-br from-gray-800 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/20">
            <h2 className="text-3xl font-bold mb-6 text-amber-300">{pageData.magazine.title}</h2>
            <h3 className="text-2xl font-bold mb-4 text-amber-200">{pageData.magazine.edition}</h3>
            <p className="text-lg mb-8 text-amber-100">
              {pageData.magazine.description}
            </p>
            
            {pageData.magazine.adOptions && pageData.magazine.adOptions.length > 0 && (
              <div className="mb-8">
                <h4 className="text-xl font-bold mb-4 text-amber-400 flex items-center gap-2">
                  <FaArrowRight className="text-amber-400" />
                  Advertisement Options
                </h4>
                <div className="space-y-3">
                  {pageData.magazine.adOptions.map((option, index) => (
                    <p key={index} className="text-amber-100">• {option.label}</p>
                  ))}
                </div>
              </div>
            )}
            
            {pageData.magazine.features && pageData.magazine.features.length > 0 && (
              <div className="mb-6">
                <h4 className="text-xl font-bold mb-4 text-amber-400 flex items-center gap-2">
                  <FaArrowRight className="text-amber-400" />
                  Why Feature?
                </h4>
                <ul className="space-y-2">
                  {pageData.magazine.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-amber-100">
                      <FaCheckCircle className="text-green-400 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {pageData.magazine.note && (
              <p className="text-amber-200 italic">
                {pageData.magazine.note}
              </p>
            )}
          </section>
        )}

        {/* Advertising Section */}
        {pageData.advertising && (
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center text-amber-300">
              {pageData.advertising.title}
            </h2>
            <div className="bg-gradient-to-br from-gray-800 to-gray-900/50 p-8 rounded-xl border border-amber-500/20">
              <div className="space-y-4">
                {pageData.advertising.options && pageData.advertising.options.length > 0 ? (
                  pageData.advertising.options.map((option, index) => (
                    <div key={index} className="p-3">
                      <p className="text-amber-100">• {option.label}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-amber-300 text-center">No advertising options available.</p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Social Media Section */}
        {pageData.social && (
          <section className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-center text-amber-300">{pageData.social.title}</h2>
            <p className="text-center text-amber-200 mb-8">
              {pageData.social.description}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {pageData.social.links && pageData.social.links.length > 0 ? (
                pageData.social.links.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-gradient-to-br from-gray-800 to-gray-900/50 px-4 py-3 rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all hover:scale-105 border border-gray-600/50"
                  >
                    <span className="text-amber-400">{getSocialIcon(social.platform)}</span>
                    <span className="text-amber-100">{social.platform}</span>
                  </a>
                ))
              ) : (
                <p className="text-amber-300">No social links available.</p>
              )}
            </div>
          </section>
        )}

        {/* Advertising Form */}
        <section id="advertising-form" className="mb-16">
          <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/30">
            <h2 className="text-3xl font-bold mb-2 text-center text-amber-300">
              MSME Times – Advertising Enquiry Form
            </h2>
            <p className="text-center text-amber-200 mb-6">
              (Lead Generation No Payment at initial Stage)
            </p>
            <h3 className="text-2xl font-bold mb-8 text-center text-amber-400">
              Book Advertising Space – MSME Times
            </h3>

            <form onSubmit={handleSubmit}>
              {/* Section 1: Basic Details */}
              <div className="mb-10">
                <h3 className="text-xl font-bold mb-6 pb-2 border-b border-amber-500/30 text-amber-300">
                  Section 1: Basic Details
                </h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-amber-300 mb-2 font-medium">
                       Full Name (Required)
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-amber-300 mb-2 font-medium">
                       Company / Brand Name (Required)
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                      placeholder="Enter your company name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-amber-300 mb-2 font-medium">
                     Email Address (Required)
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                      placeholder="Enter your email address"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-amber-300 mb-2 font-medium">
                       Mobile Number (Required)
                    </label>
                    <input
                      type="tel"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleInputChange}
                      required
                      pattern="[0-9]{10}"
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                      placeholder="Enter 10-digit mobile number"
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Business Profile */}
              <div className="mb-10">
                <h3 className="text-xl font-bold mb-6 pb-2 border-b border-amber-500/30 text-amber-300">
                  Section 2: Business Profile
                </h3>
                <div className="space-y-6">
                  <div>
                    <label className="block text-amber-300 mb-2 font-medium">
                       Business Category (Required)
                    </label>
                    <select
                      name="businessCategory"
                      value={formData.businessCategory}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                    >
                      <option value="">Select Category</option>
                      {pageData.businessCategories && pageData.businessCategories.length > 0 ? (
                        pageData.businessCategories.map((category, index) => (
                          <option key={index} value={category} className="bg-gray-800">
                            {category}
                          </option>
                        ))
                      ) : (
                        <option disabled>No categories available</option>
                      )}
                    </select>
                    
                    {formData.businessCategory === 'Other' && (
                      <div className="mt-4">
                        <input
                          type="text"
                          name="otherCategory"
                          value={formData.otherCategory}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                          placeholder="Please specify your business category"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-amber-300 mb-2 font-medium">
                       City & State (Optional)
                    </label>
                    <input
                      type="text"
                      name="cityState"
                      value={formData.cityState}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                      placeholder="Enter your city and state"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Advertising Interest */}
              <div className="mb-10">
                <h3 className="text-xl font-bold mb-6 pb-2 border-b border-amber-500/30 text-amber-300">
                  Section 3: Advertising Interest
                </h3>
                
                <div className="mb-6">
                  <label className="block text-amber-300 mb-4 font-medium">
                     Select Advertising Platforms (Multi-Select)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {pageData.advertisingPlatforms && pageData.advertisingPlatforms.length > 0 ? (
                      pageData.advertisingPlatforms.map((platform) => (
                        <label
                          key={platform.id}
                          className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-colors ${formData.selectedPlatforms.includes(platform.id) ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-gray-700/30 border border-gray-600 hover:bg-gray-700/50'}`}
                        >
                          <input
                            type="checkbox"
                            name="selectedPlatforms"
                            value={platform.id}
                            checked={formData.selectedPlatforms.includes(platform.id)}
                            onChange={handleInputChange}
                            className="text-amber-500 focus:ring-amber-500"
                          />
                          <span className="text-amber-100">{platform.label}</span>
                        </label>
                      ))
                    ) : (
                      <div className="col-span-2 text-center py-4">
                        <p className="text-amber-300">No advertising platforms available.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Conditional Logic - Magazine */}
                {isMagazineSelected && pageData.adSizes && pageData.adSizes.length > 0 && (
                  <div className="mb-6 p-6 bg-amber-500/10 rounded-lg border border-amber-500/30">
                    <p className="font-bold mb-4 text-amber-300">
                      🔹 If MSME Times Magazine (Print) is selected then Show Magazine-Specific Fields:
                    </p>
                    
                    <div className="space-y-6">
                      <div>
                        <label className="block text-amber-300 mb-2 font-medium">
                          Select Advertisement Size (Multiple select)
                        </label>
                        <div className="space-y-3">
                          {pageData.adSizes.map((size) => (
                            <label
                              key={size.value}
                              className={`flex items-center gap-3 p-4 rounded-lg cursor-pointer ${formData.adSize === size.value ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-gray-700/30 border border-gray-600 hover:bg-gray-700/50'}`}
                            >
                              <input
                                type="radio"
                                name="adSize"
                                value={size.value}
                                checked={formData.adSize === size.value}
                                onChange={handleInputChange}
                                className="text-amber-500"
                              />
                              <span className="text-amber-100"> {size.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-amber-300 mb-2 font-medium">
                           Edition Preference (Optional)
                        </label>
                        <select
                          name="editionPreference"
                          value={formData.editionPreference}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 text-white outline-none transition"
                        >
                          <option value="">Select Edition</option>
                          {pageData.editions && pageData.editions.length > 0 ? (
                            pageData.editions.map((edition, index) => (
                              <option key={index} value={edition} className="bg-gray-800"> {edition}</option>
                            ))
                          ) : (
                            <option disabled>No editions available</option>
                          )}
                        </select>
                      </div>
                      
                      {/* File Upload Section */}
                      <div>
                        <label className="block text-amber-300 mb-2 font-medium">
                           Upload Creative / Content (Optional)
                        </label>
                        <div className="mt-2">
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer bg-gray-800/30 hover:bg-gray-800/50 transition-colors">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <FaUpload className="w-8 h-8 mb-4 text-gray-400" />
                              <p className="mb-2 text-sm text-gray-400">
                                <span className="font-semibold">Click to upload</span>
                              </p>
                              <p className="text-xs text-gray-500">PDF / JPG / PNG / DOC (Max 10MB)</p>
                            </div>
                            <input
                              type="file"
                              onChange={handleFileUpload}
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            />
                          </label>
                          
                          {/* Show selected file info */}
                          {selectedFile && (
                            <div className="mt-2 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-green-400 text-sm font-medium">
                                    {selectedFile.name}
                                  </p>
                                  <p className="text-green-400/70 text-xs">
                                    {formatFileSize(selectedFile.size)}
                                  </p>
                                </div>
                                {uploadingFile && (
                                  <FaSpinner className="animate-spin text-green-400" />
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Show file error */}
                          {fileError && (
                            <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/30">
                              <p className="text-red-400 text-sm">{fileError}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Conditional Logic for Digital */}
                {isDigitalSelected && (
                  <div className="mb-6 p-6 bg-green-500/10 rounded-lg border border-green-500/30">
                    <p className="font-bold mb-4 text-green-400">
                      🔹 If any Digital / Custom Option is selected Show Custom Visibility Fields (Common for all):
                    </p>
                    
                    <div className="space-y-6">
                      <div>
                        <label className="block text-amber-300 mb-2 font-medium">
                           Visibility Objective (Dropdown)
                        </label>
                        <select
                          name="visibilityObjective"
                          value={formData.visibilityObjective}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                        >
                          <option value="">Select Objective</option>
                          {pageData.visibilityObjectives && pageData.visibilityObjectives.length > 0 ? (
                            pageData.visibilityObjectives.map((objective, index) => (
                              <option key={index} value={objective} className="bg-gray-800">{objective}</option>
                            ))
                          ) : (
                            <option disabled>No objectives available</option>
                          )}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-amber-300 mb-2 font-medium">
                           Preferred Engagement Duration (Optional)
                        </label>
                        <select
                          name="engagementDuration"
                          value={formData.engagementDuration}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                        >
                          <option value="">Select Duration</option>
                          {pageData.engagementDurations && pageData.engagementDurations.length > 0 ? (
                            pageData.engagementDurations.map((duration, index) => (
                              <option key={index} value={duration} className="bg-gray-800">{duration}</option>
                            ))
                          ) : (
                            <option disabled>No durations available</option>
                          )}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-amber-300 mb-2 font-medium">
                           Additional Notes / Requirements
                        </label>
                        <textarea
                          name="additionalNotes"
                          value={formData.additionalNotes}
                          onChange={handleInputChange}
                          rows="3"
                          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-500/20 text-white outline-none transition"
                          placeholder="Tell us briefly what kind of visibility you are looking for."
                        />
                      </div>
                    </div>
                    
                    <div className="mt-6 p-4 bg-blue-500/10 rounded border border-blue-500/30">
                      <p className="text-sm text-blue-300">
                        <strong>Info Text (Static, Below Fields):</strong><br/>
                        Digital and custom visibility options are tailored based on requirements. Our team will connect to discuss suitable plans.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 4: Consent */}
              <div className="mb-10">
                <h3 className="text-xl font-bold mb-6 pb-2 border-b border-amber-500/30 text-amber-300">
                  Section 4: Consent
                </h3>
                
                <div className="flex items-start gap-3 p-6 bg-gray-700/30 rounded-lg border border-gray-600">
                  <input
                    type="checkbox"
                    name="consentAccepted"
                    checked={formData.consentAccepted}
                    onChange={handleInputChange}
                    required
                    className="mt-1 text-amber-500 focus:ring-amber-500"
                  />
                  <div>
                    <label className="block text-amber-300 mb-2 font-medium cursor-pointer">
                       Consent Checkbox (Required)
                    </label>
                    <p className="text-amber-100">
                      I agree to be contacted by the MSME Times team regarding advertising and collaboration opportunities.
                    </p>
                  </div>
                </div>
              </div>

              {/* CAPTCHA Section */}
              <div className="mb-10">
                <div className="flex flex-col items-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpired}
                    theme="dark"
                    className={`${captchaError ? 'border-2 border-red-500 rounded' : ''}`}
                  />
                  
                  {captchaError && (
                    <p className="mt-2 text-red-400 text-sm">
                      Please complete the CAPTCHA verification
                    </p>
                  )}
                  
                  <p className="mt-4 text-sm text-amber-200/70 text-center">
                    This helps us prevent spam submissions
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.consentAccepted || uploadingFile}
                  className={`w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold py-4 px-6 rounded-lg transition-all transform focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${isSubmitting || !formData.consentAccepted || uploadingFile ? 'opacity-50 cursor-not-allowed' : 'hover:from-amber-600 hover:to-yellow-600 hover:scale-105'}`}
                >
                  {isSubmitting || uploadingFile ? (
                    <span className="flex items-center justify-center gap-2">
                      <FaSpinner className="animate-spin" />
                      {uploadingFile ? 'UPLOADING FILE...' : 'SUBMITTING...'}
                    </span>
                  ) : (
                    'SUBMIT ENQUIRY'
                  )}
                </button>
                
                {fileError && (
                  <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                    <p className="text-red-400 text-sm">{fileError}</p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </section>
      </main>

      {/* Success Popup Modal */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-amber-500/30 rounded-2xl max-w-md w-full p-8 transform animate-fade-in">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaCheckCircle className="text-2xl text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-amber-300 mb-4">
                Enquiry Submitted Successfully!
              </h3>
              
              <p className="text-amber-100 mb-6">
                Thank you for your interest in MSME Times. Our team will contact you shortly.
              </p>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowSuccessPopup(false);
                    resetForm();
                    // Redirect to /MSMETimes
                    window.location.href = '/MSMETimes';
                  }}
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 text-gray-900 font-bold py-3 px-6 rounded-lg hover:from-amber-600 hover:to-yellow-600 transition-all"
                >
                  Continue to MSME Times
                </button>
                
                <button
                  onClick={() => {
                    setShowSuccessPopup(false);
                    resetForm();
                  }}
                  className="bg-gray-700 text-amber-100 py-3 px-6 rounded-lg hover:bg-gray-600 transition-all border border-gray-600"
                >
                  Close
                </button>
              </div>
              
              <p className="text-amber-200/70 text-sm mt-6">
                You will be redirected automatically in 5 seconds...
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default MSMETimesPage;